// src/Views/StoreDetail.js
import React, { useContext } from 'react';
import { StoreContext } from '../contexts/StoreContext';
import { COLORS } from '../constants/colors'; // adjust the path as needed
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import usePageView from '../hooks/usePageView';
function to12h(time) {
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  let hour = h % 12;
  if (hour === 0) hour = 12;
  return m === 0
    ? `${hour}${suffix}`
    : `${hour}:${m < 10 ? '0' + m : m}${suffix}`;
}

// Weekday constants: 1 = Monday … 7 = Sunday
const WEEKDAYS = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 },
];

export default function StoreDetail() {
  usePageView('store');
  const { store, loading } = useContext(StoreContext);

  const getStoreLocalTimeParts = (timeZone) => {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = fmt.formatToParts(now);

    const getPart = (type) => {
      const p = parts.find((x) => x.type === type);
      return p ? parseInt(p.value, 10) : NaN;
    };

    const year = getPart('year'),
      month = getPart('month'),
      day = getPart('day'),
      hour = getPart('hour'),
      minute = getPart('minute');
    const wdName = parts.find((x) => x.type === 'weekday')?.value;
    let weekday = 1;
    if (wdName === 'Monday') weekday = 1;
    else if (wdName === 'Tuesday') weekday = 2;
    else if (wdName === 'Wednesday') weekday = 3;
    else if (wdName === 'Thursday') weekday = 4;
    else if (wdName === 'Friday') weekday = 5;
    else if (wdName === 'Saturday') weekday = 6;
    else if (wdName === 'Sunday') weekday = 7;

    const minutesSinceMidnight = hour * 60 + minute;
    return { year, month, day, weekday, minutesSinceMidnight };
  };

  const windowsForWeekday = (dailyArray, weekdayValue) => {
    return dailyArray.filter(
      ({ day, opensAt, closesAt }) => day === weekdayValue && opensAt !== closesAt
    );
  };

  const findNextOpen = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [h, m] = w.opensAt.split(':').map(Number);
      return { ...w, openMins: h * 60 + m };
    });
    todayWindows.sort((a, b) => a.openMins - b.openMins);
    for (let w of todayWindows) {
      if (w.openMins > minutesSinceMidnight) {
        return { dayOffset: 0, opensAt: w.opensAt };
      }
    }

    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = ((weekday + offset - 1) % 7) + 1;
      const nextWindows = windowsForWeekday(daily, nextDay);
      if (nextWindows.length > 0) {
        const earliest = nextWindows
          .map((w) => {
            const [h, m] = w.opensAt.split(':').map(Number);
            return { opensAt: w.opensAt, mins: h * 60 + m };
          })
          .sort((a, b) => a.mins - b.mins)[0];
        return { dayOffset: offset, opensAt: earliest.opensAt };
      }
    }

    return null;
  };

  const findCurrentClose = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [hO, mO] = w.opensAt.split(':').map(Number);
      const [hC, mC] = w.closesAt.split(':').map(Number);
      return {
        opensAt: w.opensAt,
        closesAt: w.closesAt,
        openMins: hO * 60 + mO,
        closeMins: hC * 60 + mC,
      };
    });

    for (let w of todayWindows) {
      if (w.closeMins > w.openMins) {
        if (minutesSinceMidnight >= w.openMins && minutesSinceMidnight < w.closeMins) {
          return { dayOffset: 0, closesAt: w.closesAt };
        }
      } else {
        if (minutesSinceMidnight >= w.openMins || minutesSinceMidnight < w.closeMins) {
          const offset = minutesSinceMidnight >= w.openMins ? 0 : 1;
          return { dayOffset: offset, closesAt: w.closesAt };
        }
      }
    }
    return null;
  };

  const computeOpenStatus = () => {
    if (!store?.businessHours || !Array.isArray(store.businessHours.daily)) {
      return { isOpen: false, nextChange: null };
    }
    const bh = store.businessHours;
    const { timeZone } = bh;
    const { minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const currClose = findCurrentClose(bh);
    if (currClose) {
      let [cH, cM] = currClose.closesAt.split(':').map(Number);
      let closeMins = cH * 60 + cM;
      if (currClose.dayOffset === 1) {
        closeMins += 24 * 60;
      }
      const nowMinsAdjusted =
        currClose.dayOffset === 1 && minutesSinceMidnight < closeMins % (24 * 60)
          ? minutesSinceMidnight + 24 * 60
          : minutesSinceMidnight;
      const minutesUntilClose = closeMins - nowMinsAdjusted;
      return {
        isOpen: true,
        nextChange: {
          type: 'closing',
          timeString: to12h(currClose.closesAt),
          minutesUntil: minutesUntilClose,
        },
      };
    }

    const nextOpen = findNextOpen(bh);
    if (!nextOpen) {
      return { isOpen: false, nextChange: null };
    }
    const { dayOffset, opensAt } = nextOpen;
    let [oH, oM] = opensAt.split(':').map(Number);
    let openMins = oH * 60 + oM + dayOffset * 24 * 60;
    const minutesUntilOpen = openMins - minutesSinceMidnight;
    return {
      isOpen: false,
      nextChange: {
        type: 'opening',
        timeString: to12h(opensAt),
        minutesUntil: minutesUntilOpen,
      },
    };
  };

  const hasBusinessHours =
    !!store?.businessHours &&
    Array.isArray(store.businessHours.daily) &&
    store.businessHours.daily.some(({ opensAt, closesAt }) => opensAt !== closesAt);

  const { isOpen, nextChange } = hasBusinessHours
    ? computeOpenStatus()
    : { isOpen: false, nextChange: null };

  let statusText = '';
  if (hasBusinessHours) {
    statusText = 'Closed';
    if (isOpen && nextChange) {
      statusText = `Open until ${nextChange.timeString}`;
    } else if (!isOpen && nextChange) {
      statusText = `Opens at ${nextChange.timeString}`;
    }
  }

  const renderHoursGrid = () => {
    if (!store.businessHours || !Array.isArray(store.businessHours.daily)) {
      return null;
    }
    const bh = store.businessHours;
    const { timeZone, daily } = bh;
    const { year, month, day, weekday } = getStoreLocalTimeParts(timeZone);

    const isoDateString = `${year}-${String(month).padStart(2, '0')}-${String(
      day
    ).padStart(2, '0')}T12:00:00`;
    const localNoon = new Date(isoDateString);

    return WEEKDAYS.map(({ label, value }) => {
      const delta = value - weekday;
      const candidate = new Date(localNoon);
      candidate.setDate(localNoon.getDate() + delta);

      const dateFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        month: 'long',
        day: 'numeric',
      });
      const formattedDate = dateFormatter.format(candidate);

      const isToday = delta === 0;
      const windows = windowsForWeekday(daily, value);
      let timeText = 'Closed';
      if (windows.length > 0) {
        timeText = windows
          .map((w) => `${to12h(w.opensAt)}–${to12h(w.closesAt)}`)
          .join(', ');
      }

      return (
        <React.Fragment key={value}>
          <div className={`day-cell ${isToday ? 'today' : ''}`}>
            {isToday ? 'Today' : label}
          </div>
          <div className={`date-cell ${isToday ? 'today' : ''}`}>
            {formattedDate}
          </div>
          <div className={`time-cell ${isToday ? 'today' : ''}`}>
            {timeText}
          </div>
        </React.Fragment>
      );
    });
  };

  const mapsUrl = () => {
    if (!store?.location) return '#';
    const { subThoroughfare, thoroughfare, locality, administrativeArea, postalCode } =
      store.location;

    const addressString = encodeURIComponent(
      `${subThoroughfare} ${thoroughfare} ${locality} ${administrativeArea} ${postalCode}`
    );
    return `https://www.google.com/maps/search/?api=1&query=${addressString}`;
  };

  const embedUrl = () => {
    if (!store?.location) return '';
    const { subThoroughfare, thoroughfare, locality, administrativeArea, postalCode } =
      store.location;

    const addressString = encodeURIComponent(
      `${subThoroughfare} ${thoroughfare} ${locality} ${administrativeArea} ${postalCode}`
    );
    return `https://www.google.com/maps?q=${addressString}&output=embed`;
  };

  return (
    <div className={`store-detail-container ${loading ? 'loading' : ''}`}>
      {loading && (
        <div className="inline-loading">
          <div className="spinner" />
        </div>
      )}

      {!loading && !store && <p className="not-found">Store not found.</p>}

      {!loading && store && (
        <div>
          {/* ―― STATUS BADGE & STORE NAME (CENTERED with extra top padding) ―― */}
          <div className="header-area">
           
            <h1 className="store-name">{store.name}</h1>
            {hasBusinessHours && (
              <div className={`badge ${isOpen ? 'open' : 'closed'}`}>{statusText}</div>
            )}
          </div>

          {/* ―― CONTACT + MAP SIDE BY SIDE ―― */}
          <div className="contact-map-wrapper">
            {(store.storeContact || store.location) && (
              <div className="card contact-card">
                <h2 className="section-heading">
                  {store.storeContact?.name
                    ? `Contact ${store.storeContact.name}`
                    : 'Contact'}
                </h2>

                {store.storeContact?.phoneNumber && (
                  <p className="contact-info">
                    <span className="material-icons icon">phone</span>
                    <a
                      href={`tel:${(
                        store.storeContact.phoneNumber || ''
                      ).replace(/\D+/g, '')}`}
                      className="link"
                    >
                      {store.storeContact.phoneNumber}
                    </a>
                  </p>
                )}

                {store.storeContact?.email && (
                  <p className="contact-info">
                    <span className="material-icons icon">email</span>
                    <a href={`mailto:${store.storeContact.email}`} className="link">
                      {store.storeContact.email}
                    </a>
                  </p>
                )}

                {store.location && (
                  <p className="contact-info">
                    <span className="material-icons icon">place</span>
                    <a
                      href={mapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                    >
                      {`${store.location.subThoroughfare} ${store.location.thoroughfare}, ${store.location.locality}, ${store.location.administrativeArea} ${store.location.postalCode}`}
                    </a>
                  </p>
                )}
              </div>
            )}

            {store.location && (
              <div className="card map-card">
                <iframe
                  title="store-map"
                  src={embedUrl()}
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>
            )}
          </div>

          {store.businessHours && Array.isArray(store.businessHours.daily) && (
            <div className="card hours-card">
              <h2 className="section-heading">Hours</h2>
              <div className="hours-grid">{renderHoursGrid()}</div>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .store-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: 0 16px 48px;
          padding-left: max(16px, env(safe-area-inset-left));
          padding-right: max(16px, env(safe-area-inset-right));
        }

        .store-detail-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }
        .inline-loading {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-top-color: #333;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .not-found {
          text-align: center;
          color: #666;
          margin-top: 32px;
          font-size: 1.1rem;
        }

        .header-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding-top: 80px;
          padding-bottom: 80px;
        }

        .badge {
          font-size: 1rem;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 999px;
          text-transform: uppercase;
          display: inline-block;
        }
        .badge.open {
          background-color: #d1fae5;
          color: #065f46;
        }
        .badge.closed {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .store-name {
    margin: 0;
    font-size: 2.25rem;
    font-weight: 900;
    font-family: 'Instrument Serif', serif;
    color: #111827;
    text-align: center;
    text-transform: uppercase;
  }

        .contact-map-wrapper {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        /* Removed box-shadow here */
        .card {
          background-color: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .contact-card {
          flex: 1;
          padding: 24px 32px;
        }
        .contact-info {
          margin: 8px 0;
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #1f2937;
        }
      .contact-info .icon {
    font-size: 1.25rem;
    color: ${COLORS.primary};
  }
       .contact-info .link {
    color: ${COLORS.primary};
    text-decoration: none;
  }
        .contact-info .link:hover {
          text-decoration: underline;
        }

        .map-card {
          flex: 1;
          overflow: hidden;
          height: 200px;
        }

        .hours-card {
          padding: 24px 32px;
        }
        .section-heading {
          margin: 0 0 12px 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 4px;
        }
        .hours-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 2fr;
          column-gap: 16px;
          row-gap: 8px;
          font-size: 0.95rem;
        }
        .day-cell {
          font-weight: 500;
          color: #374151;
        }
        .date-cell {
          color: #4b5563;
        }
        .time-cell {
          color: #4b5563;
        }
        .closed-day {
          color: #9ca3af;
        }
        .today {
          font-weight: 700;
          color: #111827;
        }

        @media (max-width: 600px) {
          .contact-map-wrapper {
            flex-direction: column;
          }
          .map-card {
            height: 200px;
          }
          .hours-card {
            padding: 16px 20px;
          }
          .contact-card {
            padding: 16px 20px;
          }
          .hours-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
